import React, { useState, useEffect, useMemo } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Check, ChevronsUpDown, Plus, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";

const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
function similarTo(name, list) {
  const n = norm(name);
  if (!n) return [];
  return list.filter((c) => {
    const cn = norm(c.name);
    return cn && (cn === n || cn.includes(n) || n.includes(cn));
  });
}

// Searchable category picker sourced from the tenant's categories PLUS the full
// IFRS catalog (Configure list). Selecting a catalog item that isn't added yet
// creates it. Typing a new name adds a custom category. Warns on same/similar.
export function CategoryCombobox({ cats, value, onChange, onCreated }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [catalog, setCatalog] = useState([]);
  const [pending, setPending] = useState(null); // {name, meta, similar}
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || catalog.length) return;
    api.get("/m1/categories/catalog").then((r) => {
      const flat = [];
      (r.data.groups || []).forEach((g) => (g.items || []).forEach((it) =>
        flat.push({ code: it.code, name: it.name, standard: it.standard, tier: g.tier, present: it.present })));
      setCatalog(flat);
    }).catch((e) => toast.error(apiError(e)));
  }, [open, catalog.length]);

  const existing = useMemo(() => cats.filter((c) => c.active !== false), [cats]);
  const existingNames = useMemo(() => new Set(existing.map((c) => c.name.toLowerCase())), [existing]);
  const suggestions = useMemo(
    () => catalog.filter((it) => !existingNames.has(it.name.toLowerCase())),
    [catalog, existingNames]);

  const selected = existing.find((c) => c.id === value);
  const q = query.trim();
  const exactExisting = existing.find((c) => c.name.toLowerCase() === q.toLowerCase());
  const exactSuggestion = suggestions.find((c) => c.name.toLowerCase() === q.toLowerCase());
  const showAddCustom = q && !exactExisting && !exactSuggestion;

  const doCreate = async (name, meta) => {
    setBusy(true);
    try {
      const r = await api.post("/m1/categories", { name, ...(meta || {}) });
      toast.success(`Category "${name}" added`);
      onChange(r.data.id);
      onCreated && onCreated();
      setQuery(""); setPending(null); setOpen(false);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  // Route every creation through a same/similar check against existing categories.
  const requestCreate = (name, meta) => {
    const sim = similarTo(name, existing);
    if (sim.length) { setOpen(false); setPending({ name, meta, similar: sim }); }
    else doCreate(name, meta);
  };

  const pickSuggestion = (it) =>
    requestCreate(it.name, { code: it.code, tier: it.tier, standard: it.standard, source: "ifrs" });

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" role="combobox" data-testid="req-category"
            className="mt-1.5 w-full justify-between font-normal">
            <span className={cn("truncate", !selected && "text-slate-400")}>{selected ? selected.name : "None"}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command shouldFilter={true}>
            <CommandInput placeholder="Search categories…" value={query} onValueChange={setQuery} data-testid="req-category-search" />
            <CommandList>
              <CommandEmpty>
                {q ? (
                  <button type="button" onClick={() => requestCreate(q)} disabled={busy} data-testid="req-category-add"
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950/40">
                    <Plus className="h-4 w-4" /> Add “{q}”
                  </button>
                ) : <span className="px-3 py-2 text-sm text-slate-400">No categories</span>}
              </CommandEmpty>

              {value && (
                <CommandItem value="__none__" onSelect={() => { onChange(""); setOpen(false); }} data-testid="req-category-none">
                  <span className="ml-6 text-slate-400">None</span>
                </CommandItem>
              )}

              {existing.length > 0 && (
                <CommandGroup heading="Your categories">
                  {existing.map((c) => (
                    <CommandItem key={c.id} value={c.name} data-testid={`req-category-opt-${c.id}`}
                      onSelect={() => { onChange(c.id); setOpen(false); }}>
                      <Check className={cn("mr-2 h-4 w-4", value === c.id ? "opacity-100" : "opacity-0")} />
                      {c.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {suggestions.length > 0 && (
                <CommandGroup heading="IFRS catalog — select to add">
                  {suggestions.map((it) => (
                    <CommandItem key={it.code} value={it.name} data-testid={`req-category-cat-${it.code}`}
                      onSelect={() => pickSuggestion(it)}>
                      <Plus className="mr-2 h-4 w-4 text-indigo-500" />
                      <span className="flex-1">{it.name}</span>
                      {it.standard && <span className="ml-2 text-[10px] text-emerald-600 font-semibold">{it.standard}</span>}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {showAddCustom && (
                <CommandGroup>
                  <CommandItem value={`__add__${q}`} onSelect={() => requestCreate(q)} data-testid="req-category-add-inline" className="text-indigo-600">
                    <Plus className="mr-2 h-4 w-4" /> Add “{q}” as a custom category
                  </CommandItem>
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <Dialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <DialogContent data-testid="cat-similar-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600"><AlertTriangle className="h-5 w-5" /> Similar category exists</DialogTitle>
            <DialogDescription>
              A same or similar category already exists for “{pending?.name}”:
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap gap-2">
            {pending?.similar.map((c) => (
              <button key={c.id} data-testid={`cat-use-existing-${c.id}`}
                onClick={() => { onChange(c.id); setPending(null); setQuery(""); }}
                className="rounded-full bg-indigo-50 dark:bg-indigo-950/40 px-3 py-1 text-sm font-medium text-indigo-700 hover:bg-indigo-100">
                Use “{c.name}”
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPending(null)}>Cancel</Button>
            <Button data-testid="cat-create-anyway" disabled={busy}
              onClick={() => doCreate(pending.name, pending.meta)}
              className="bg-indigo-600 hover:bg-indigo-700">Create anyway</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
